import axios, {AxiosInstance} from "axios";
import keywords from "./assets/keywords.json";
import i18n from "./i18n";

// Only log in development — keeps the production console clean
const isDev = process.env.NODE_ENV === "development";
const devLog = (...args: unknown[]) => { if (isDev) console.log(...args); };
const devError = (...args: unknown[]) => { if (isDev) console.error(...args); };

interface Keyword {
  id: number;
  name: string;
}
interface Rating {
  iso_3166_1: string;
  rating: string;
  [key: string]: any;
}
interface Video {
  name: string;
  key: string;
  type?: string;
  site?: string;
  [key: string]: any;
}

interface CastMember {
  id: number;
  name: string;
  character: string;
  profile_path: string | null;
  media_type: string;
}

interface CrewMember {
  id: number;
  name: string;
  job: string;
  department: string;
  profile_path: string | null;
}

interface CreditsResponse {
  cast: CastMember[];
  crew: CrewMember[];
}

interface GetCurrentContentOptions {
  search?: string;
  with_keywords?: string;
  with_genres?: string;
  sort_by?: string;
  vote_average_gte?: string;
  vote_average_lte?: string;
  page?: number;
}

interface DiscoverResponse {
  results: any[];
  total_pages: number;
}

interface ContentDetails {
  id: number;
  title?: string;
  name?: string;
  keywords: {id: number; name: string}[];
  images: {backdrops: any[]; posters: any[]};
  videos: {results: {name: string; key: string}[]};
  video_url: {key: string};
  [key: string]: any;
  reviews: [];
  poster_path: string;
  similar: {}[];
}

// Mapping i18n short codes to TMDB full codes
const TMDB_LANGUAGE_MAP: Record<string, string> = {
  en: "en-US",
  es: "es-ES",
  fr: "fr-FR",
  ru: "ru-RU",
};

export const defaultParams = {
  page: 1,
  get language() {
    return TMDB_LANGUAGE_MAP[i18n.language] || "en-US"; // Default to "en-US"
  },
};

export const tmdb_api: AxiosInstance = axios.create({
  baseURL: "https://api.themoviedb.org/3/",
  params: {
    api_key: process.env.REACT_APP_MOVIES_API_KEY,
  },
});

const omdb_api: AxiosInstance = axios.create({
  baseURL: "https://omdbapi.com/",
  params: {
    apikey: process.env.REACT_APP_MOVIES_OMDB_API_KEY,
  },
});
export const getCollectionDetails = async (collectionId: number) => {
  try {
    const response = await tmdb_api.get(`/collection/${collectionId}`, {
      params: {...defaultParams},
    });
    return response.data;
  } catch (error) {
    devError(`Failed to fetch collection details for ID ${collectionId}:`, error);
    throw error;
  }
};

export const getRecommendations = async (
  movieId: string,
  contentType: "movie" | "tv",
  page: number
) => {
  try {
    // TMDB /recommendations is curated and much higher quality than /similar
    const recResponse = await tmdb_api.get(`/${contentType}/${movieId}/recommendations`, {
      params: {...defaultParams, page},
    });

    const recResults: any[] = recResponse.data.results ?? [];
    const totalPages: number = recResponse.data.total_pages ?? 1;

    // If recommendations are sparse, supplement with /similar (genre-based)
    if (recResults.length < 10 && page === 1) {
      try {
        const simResponse = await tmdb_api.get(`/${contentType}/${movieId}/similar`, {
          params: {...defaultParams, page: 1},
        });
        const simResults: any[] = simResponse.data.results ?? [];
        const seen = new Set(recResults.map((r: any) => r.id));
        const extra = simResults.filter((r: any) => !seen.has(r.id));
        recResults.push(...extra);
      } catch {
        // ignore if similar fails
      }
    }

    return {results: recResults, total_pages: Math.max(totalPages, 1)};
  } catch (error) {
    devError(`Failed to fetch recommendations for ${movieId}:`, error);
    throw error;
  }
};

export const findKeywordIdByName = (name: string): number | null => {
  if (!name) return null;
  const keyword = (keywords as Keyword[]).find((k) => k.name.toLowerCase() === name.toLowerCase());
  return keyword ? keyword.id : null;
};

export const findKeywordIdsString = (keywordNames: string = ""): string | null => {
  if (!keywordNames) return null;

  const keywordIds = keywordNames
    .split(",")
    .map((name) => {
      const keyword = (keywords as Keyword[]).find((k) => k.name.toLowerCase().trim() === name.toLowerCase().trim());
      return keyword ? keyword.id : null;
    })
    .filter((id): id is number => id !== null);

  return keywordIds.join(",");
};

export const getPerson = async (personId: number): Promise<any> => {
  try {
    const response = await tmdb_api.get(`/person/${personId}`, {
      params: {
        ...defaultParams,
        append_to_response: "combined_credits,external_ids",
      },
    });
    return response.data;
  } catch (error) {
    devError(`Failed to fetch person with ID ${personId}:`, error);
    throw error;
  }
};

export const discover = async (
  contentType: string,
  options: GetCurrentContentOptions = {}
): Promise<DiscoverResponse> => {
  try {
    devLog("filters", options);
    let results: any[] = [];
    let page = 1;
    let totalPages = 1;

    if (options.search) {
      while (page <= totalPages) {
        const response = await tmdb_api.get(`/search/${contentType}`, {
          params: {
            ...defaultParams,
            query: options.search,
            page,
          },
        });
        results = [...results, ...response.data.results];
        totalPages = response.data.total_pages;
        page++;
        if (page > 5) break;
      }
      return {results, total_pages: 1};
    }

    const findedKeywords = findKeywordIdsString(options.with_keywords);
    const response = await tmdb_api.get(`/discover/${contentType}`, {
      params: {
        ...defaultParams,
        page: options.page || 1,
        sort_by: options.sort_by || "popularity.desc",
        with_genres: options.with_genres || null,
        with_keywords: findedKeywords || null,
        vote_average_gte: options.vote_average_gte || null,
        vote_average_lte: options.vote_average_lte || null,
      },
    });

    return response.data;
  } catch (error) {
    devError("Error fetching items:", error);
    return {results: [], total_pages: 0};
  }
};

export const getMainPageMovies = async (): Promise<{
  latestMovies: any[];
  topShows: any[];
  popularMovies: any[];
  upcomingMovies: any[];
  popularShows: any[];
}> => {
  const [np_movies, tp_shows, pop_movies, up_movies, pop_shows] = await Promise.all([
    tmdb_api.get("movie/now_playing", {params: defaultParams}),
    tmdb_api.get("tv/top_rated", {params: defaultParams}),
    tmdb_api.get("movie/popular", {params: defaultParams}),
    tmdb_api.get("movie/upcoming", {params: defaultParams}),
    tmdb_api.get("tv/popular", {params: defaultParams}),
  ]);

  return {
    latestMovies: np_movies.data.results,
    topShows: tp_shows.data.results,
    popularMovies: pop_movies.data.results,
    upcomingMovies: up_movies.data.results,
    popularShows: pop_shows.data.results,
  };
};

export const searchMulti = async (query: string, page: number = 1): Promise<{results: any[]; total_pages: number}> => {
  try {
    const response = await tmdb_api.get("/search/multi", {
      params: {...defaultParams, query, page},
    });
    return response.data;
  } catch (error) {
    devError("Error searching:", error);
    return {results: [], total_pages: 0};
  }
};

export const searchKeyword = async (query: string): Promise<any[]> => {
  try {
    const response = await tmdb_api.get("/search/keyword", {
      params: {...defaultParams, query},
    });
    return response.data.results;
  } catch (error) {
    devError("Error searching for keyword:", error);
    throw error;
  }
};

export const getTVGenres = async (): Promise<any[]> => {
  try {
    const response = await tmdb_api.get("/genre/tv/list");
    return response.data.genres;
  } catch (error) {
    devError("Error fetching TV genres:", error);
    throw error;
  }
};

export const getCurrentContent = async (
  contentType: string,
  contentId: number,
  title: string = ""
): Promise<ContentDetails> => {
  try {
    const isMovie = contentType === "movie";

    const [contentResponse, imagesResponse] = await Promise.all([
      tmdb_api.get(`${contentType}/${contentId}`, {
        params: {
          ...defaultParams,
          append_to_response:
            "keywords,reviews,external_ids,recommendations,credits,videos,watch/providers" +
            (isMovie ? ",images" : ",content_ratings"),
        },
      }),
      tmdb_api.get(`${contentType}/${contentId}/images`, {
        params: {include_image_language: defaultParams.language},
      }),
    ]);

    const video = contentResponse.data.videos.results.find(
      (video: Video) => video.name === "Official Trailer" || video.name === "Trailer"
    );

    const additionalData: Record<string, any> = {};

    if (!isMovie) {
      additionalData.ratings = contentResponse.data.content_ratings.results.filter(
        (rating: Rating) => rating.iso_3166_1 === "US"
      );
    } else if (title) {
      const omdbResponse = await omdb_api.get("/", {params: {t: title}});
      additionalData.omdbDetails = {
        awards: omdbResponse.data.Awards,
        director: omdbResponse.data.Director,
        writer: omdbResponse.data.Writer,
        actors: omdbResponse.data.Actors,
        rated: omdbResponse.data.Rated,
        language: omdbResponse.data.Language,
      };
    }

    // Fetch cross-type recommendations based on first genre for a mixed result set
    const firstGenreId = contentResponse.data.genres?.[0]?.id;
    const crossType = isMovie ? "tv" : "movie";
    let crossTypeItems: any[] = [];
    if (firstGenreId) {
      try {
        const crossResponse = await tmdb_api.get(`/discover/${crossType}`, {
          params: {
            ...defaultParams,
            with_genres: firstGenreId,
            sort_by: "vote_count.desc",
            "vote_count.gte": 100,
          },
        });
        crossTypeItems = (crossResponse.data.results ?? []).slice(0, 10);
      } catch {
        // cross-type fetch is best-effort
      }
    }

    // Interleave same-type recommendations with cross-type items
    const sameTypeItems: any[] = (contentResponse.data.recommendations?.results ?? []).slice(0, 10);
    const combined: any[] = [];
    const maxLen = Math.max(sameTypeItems.length, crossTypeItems.length);
    for (let i = 0; i < maxLen; i++) {
      if (sameTypeItems[i]) combined.push(sameTypeItems[i]);
      if (crossTypeItems[i]) combined.push(crossTypeItems[i]);
    }

    return {
      ...contentResponse.data,
      keywords: isMovie ? contentResponse.data.keywords.keywords : contentResponse.data.keywords.results,
      socials: contentResponse.data.external_ids,
      reviews: contentResponse.data.reviews.results,
      similar: combined,
      credits: contentResponse.data.credits,
      video_url: video ? `https://www.youtube.com/watch?v=${video.key}` : null,
      images: imagesResponse.data,
      ...additionalData,
    };
  } catch (error) {
    devError(`Failed to fetch ${contentType} with ID ${contentId}:`, error);
    throw error;
  }
};

export const getTrending = async (contentType: string, page: number = 1): Promise<DiscoverResponse> => {
  try {
    const response = await tmdb_api.get(`/trending/${contentType}/week`, {params: {...defaultParams, page}});
    return response.data;
  } catch (error) {
    devError(`Error fetching trending ${contentType}:`, error);
    throw error;
  }
};

export const getPopular = async (contentType: string, page: number = 1): Promise<DiscoverResponse> => {
  try {
    const response = await tmdb_api.get(`/${contentType}/popular`, {params: {...defaultParams, page}});
    return response.data;
  } catch (error) {
    devError(`Error fetching popular ${contentType}:`, error);
    throw error;
  }
};

export const getTopRated = async (contentType: string, page: number = 1): Promise<DiscoverResponse> => {
  try {
    const response = await tmdb_api.get(`/${contentType}/top_rated`, {params: {...defaultParams, page}});
    return response.data;
  } catch (error) {
    devError(`Error fetching top-rated ${contentType}:`, error);
    throw error;
  }
};

export const getUpcoming = async (content_type: "movie" | "tv", page: number = 1): Promise<DiscoverResponse> => {
  try {
    const response = await tmdb_api.get(content_type === "movie" ? `/movie/upcoming` : "/tv/on_the_air", {
      params: {...defaultParams, page},
    });

    return response.data;
  } catch (error) {
    devError("Error fetching upcoming movies:", error);
    throw error;
  }
};

export const getCredits = async (contentType: string, contentId: number): Promise<CreditsResponse> => {
  try {
    const endpoint = contentType === "movie" ? `movie/${contentId}/credits` : `tv/${contentId}/aggregate_credits`;
    const response = await tmdb_api.get(endpoint, {params: defaultParams});
    return {cast: response.data.cast, crew: response.data.crew};
  } catch (error) {
    devError(`Failed to fetch ${contentType} credits for ID ${contentId}:`, error);
    throw error;
  }
};

export const getContentDetails = async (contentType: string, contentId: number): Promise<ContentDetails> => {
  try {
    const response = await tmdb_api.get(`${contentType}/${contentId}`, {
      params: {
        ...defaultParams,
        append_to_response: "images,keywords,videos",
      },
    });

    const video = response.data.videos.results.find(
      (video: Video) => video.name === "Official Trailer" || video.name === "Trailer"
    );

    return {
      ...response.data,
      video_url: video ? `https://www.youtube.com/watch?v=${video.key}` : null,
    };
  } catch (error) {
    devError(`Failed to fetch ${contentType} details for ID ${contentId}:`, error);
    throw error;
  }
};

export const getTrailerKey = async (contentType: string, contentId: number): Promise<string | null> => {
  try {
    const response = await tmdb_api.get(`/${contentType}/${contentId}/videos`, {
      params: defaultParams,
    });
    const videos: Video[] = response.data.results ?? [];
    const trailer =
      videos.find((v) => v.type === "Trailer" && v.site === "YouTube") ??
      videos.find((v) => v.site === "YouTube");
    return trailer?.key ?? null;
  } catch {
    return null;
  }
};

export const getSeasonTrailerKey = async (showId: number, seasonNumber: number): Promise<string | null> => {
  try {
    const response = await tmdb_api.get(`/tv/${showId}/season/${seasonNumber}/videos`, {
      params: defaultParams,
    });
    const videos: Video[] = response.data.results ?? [];
    const trailer =
      videos.find((v) => v.type === "Trailer" && v.site === "YouTube") ??
      videos.find((v) => v.type === "Teaser" && v.site === "YouTube") ??
      videos.find((v) => v.site === "YouTube");
    return trailer?.key ?? null;
  } catch {
    return null;
  }
};

export interface PersonalRecItem {
  id: number;
  title?: string;
  name?: string;
  poster_path?: string | null;
  release_date?: string | null;
  first_air_date?: string | null;
  vote_average?: number;
  vote_count?: number;
}

export const getPersonalizedRecs = async (
  sourceItems: Array<{id: number; type: "movie" | "tv"; vote_average?: number; title?: string; name?: string}>,
  excludeIds: Set<number>,
  contentType: "movie" | "tv",
  userRatings?: Record<number, number>
): Promise<{results: PersonalRecItem[]; sourceLabels: string[]; bySource: Array<{label: string; items: PersonalRecItem[]}>}> => {
  // Rating weight: 5★ → 2.0x, 4★ → 1.5x, 3★ → 1.0x, 2★ → 0.7x, 1★ → 0.4x, unrated → 1.0x
  const ratingWeight = (id: number) => {
    const r = userRatings?.[id];
    if (!r) return 1.0;
    return [0, 0.4, 0.7, 1.0, 1.5, 2.0][r] ?? 1.0;
  };

  const sources = [...sourceItems]
    .sort((a, b) => {
      const wa = ratingWeight(a.id) * (a.vote_average ?? 5);
      const wb = ratingWeight(b.id) * (b.vote_average ?? 5);
      return wb - wa;
    })
    .slice(0, 15);

  if (sources.length === 0) {
    const fallback = await getTrending(contentType, 1);
    return {results: fallback.results as PersonalRecItem[], sourceLabels: [], bySource: []};
  }

  const sameType = sources.filter((s) => s.type === contentType);
  const crossType = sources.filter((s) => s.type !== contentType);

  // Same-type: direct TMDB recommendations
  const sameTypePromise = Promise.allSettled(
    sameType.map((src) =>
      tmdb_api
        .get(`/${contentType}/${src.id}/recommendations`, {params: {...defaultParams, page: 1}})
        .then((r) => ({src, results: r.data.results as PersonalRecItem[]}))
        .catch(() => ({src, results: [] as PersonalRecItem[]}))
    )
  );

  // Cross-type: fetch genres for each source, then run a single discover call
  const crossTypePromise: Promise<PersonalRecItem[]> = crossType.length > 0
    ? Promise.allSettled(
        crossType.map((src) =>
          tmdb_api
            .get(`/${src.type}/${src.id}`, {params: defaultParams})
            .then((r) => (r.data.genres ?? []).map((g: {id: number}) => g.id))
            .catch(() => [] as number[])
        )
      ).then(async (genreResults) => {
        const genreIds = [
          ...new Set(
            genreResults.flatMap((r) => (r.status === "fulfilled" ? r.value : []))
          ),
        ].slice(0, 5);
        if (genreIds.length === 0) return [];
        const r = await tmdb_api
          .get(`/discover/${contentType}`, {
            params: {
              ...defaultParams,
              with_genres: genreIds.join(","),
              sort_by: "vote_count.desc",
              "vote_count.gte": 50,
              page: 1,
            },
          })
          .catch(() => ({data: {results: []}}));
        return r.data.results as PersonalRecItem[];
      })
    : Promise.resolve([] as PersonalRecItem[]);

  const [sameTypeSettled, crossTypeItems] = await Promise.all([sameTypePromise, crossTypePromise]);

  const scoreMap = new Map<number, {item: PersonalRecItem; freq: number; totalScore: number; primarySource: string}>();

  sameTypeSettled.forEach((result) => {
    if (result.status !== "fulfilled") return;
    const {src, results} = result.value;
    const weight = ratingWeight(src.id);
    const srcLabel = src.title || src.name || "";
    results.forEach((item) => {
      if (excludeIds.has(item.id)) return;
      const existing = scoreMap.get(item.id);
      if (existing) {
        existing.freq++;
        existing.totalScore += (item.vote_average ?? 0) * weight;
      } else {
        scoreMap.set(item.id, {item, freq: 1, totalScore: (item.vote_average ?? 0) * weight, primarySource: srcLabel});
      }
    });
  });

  // Cross-type discover results added at base weight (skip anime)
  crossTypeItems.forEach((item) => {
    if (excludeIds.has(item.id)) return;
    if ((item as any).genre_ids?.includes(16) && (item as any).origin_country?.includes("JP")) return;
    if (!scoreMap.has(item.id)) {
      scoreMap.set(item.id, {item, freq: 1, totalScore: item.vote_average ?? 0, primarySource: ""});
    }
  });

  // Exclude anime (Animation genre 16 + Japanese origin) — those belong to the dedicated Anime page.
  const isAnime = (item: PersonalRecItem) =>
    (item as any).genre_ids?.includes(16) &&
    ((item as any).origin_country?.includes("JP") ?? false);

  const sorted = Array.from(scoreMap.values())
    .filter((e) => !isAnime(e.item))
    .sort((a, b) => b.totalScore - a.totalScore || b.freq - a.freq)
    .map((e) => e.item);

  if (sorted.length === 0) {
    const fallback = await getTrending(contentType, 1);
    return {results: fallback.results as PersonalRecItem[], sourceLabels: [], bySource: []};
  }

  const sourceLabels = sources
    .map((s) => s.title || s.name || "")
    .filter(Boolean)
    .slice(0, 5);

  // Build by-source groups for "Because you liked X" UI
  const groupMap = new Map<string, PersonalRecItem[]>();
  Array.from(scoreMap.values())
    .sort((a, b) => b.totalScore - a.totalScore || b.freq - a.freq)
    .forEach(({item, primarySource}) => {
      if (!primarySource) return;
      const arr = groupMap.get(primarySource) ?? [];
      arr.push(item);
      groupMap.set(primarySource, arr);
    });
  const bySource = Array.from(groupMap.entries())
    .map(([label, items]) => ({label, items: items.slice(0, 10)}))
    .slice(0, 5);

  return {results: sorted, sourceLabels, bySource};
};

// ── Anime ─────────────────────────────────────────────────────────────────────
// Anime on TMDB = tv shows with Animation genre (16) from Japan.
// ANIME_GENRES maps user-facing labels to TMDB TV genre IDs.
export const ANIME_GENRES: {id: number; label: string}[] = [
  {id: 10759, label: "Action"},
  {id: 35, label: "Comedy"},
  {id: 10749, label: "Romance"},
  {id: 18, label: "Drama"},
  {id: 10765, label: "Fantasy"},
  {id: 9648, label: "Mystery"},
  {id: 10762, label: "Kids"},
];

export const getAnime = async (
  category: "popular" | "best" | "airing" | "trending",
  page: number = 1,
  genreId?: number
): Promise<DiscoverResponse> => {
  try {
    const sortMap: Record<string, string> = {
      popular: "popularity.desc",
      best: "vote_average.desc",
      airing: "first_air_date.desc",
      trending: "popularity.desc",
    };
    const withGenres = genreId ? `16,${genreId}` : 16;
    const response = await tmdb_api.get("/discover/tv", {
      params: {
        ...defaultParams,
        page,
        with_genres: withGenres,
        with_origin_country: "JP",
        sort_by: sortMap[category] ?? "popularity.desc",
        ...(category === "best" ? {"vote_count.gte": 200} : {}),
        ...(category === "airing" ? {with_status: "0"} : {}),
      },
    });
    return response.data;
  } catch (error) {
    devError("Error fetching anime:", error);
    return {results: [], total_pages: 0};
  }
};

export const getAnimeRecommendations = async (
  sourceItems: Array<{id: number; vote_average?: number; title?: string; name?: string}>,
  excludeIds: Set<number>,
  animeOnly: boolean = true
): Promise<PersonalRecItem[]> => {
  if (sourceItems.length === 0) {
    const r = await getAnime("popular", 1);
    return r.results as PersonalRecItem[];
  }
  const seeds = [...sourceItems]
    .sort((a, b) => (b.vote_average ?? 0) - (a.vote_average ?? 0))
    .slice(0, 10);

  const settled = await Promise.allSettled(
    seeds.map((src) =>
      tmdb_api
        .get(`/tv/${src.id}/recommendations`, {params: {...defaultParams, page: 1}})
        .then((r) => (r.data.results as PersonalRecItem[]).filter((i) => !excludeIds.has(i.id)))
        .catch(() => [] as PersonalRecItem[])
    )
  );

  // Score = position weight (TMDB's own similarity rank) + frequency bonus + small vote_average nudge.
  // Position weight: 1st result = 1.0, 2nd = 0.5, 3rd = 0.33 … so TMDB order is preserved.
  const scoreMap = new Map<number, {item: PersonalRecItem; score: number}>();
  settled.forEach((r) => {
    if (r.status !== "fulfilled") return;
    r.value
      .filter((item) => !animeOnly || (item as any).genre_ids?.includes(16))
      .forEach((item, idx) => {
        const posWeight = 1 / (idx + 1);
        const existing = scoreMap.get(item.id);
        if (existing) {
          existing.score += posWeight + 0.5;
        } else {
          const voteNudge = (item.vote_average ?? 0) * 0.02;
          scoreMap.set(item.id, {item, score: posWeight + voteNudge});
        }
      });
  });
  return Array.from(scoreMap.values())
    .sort((a, b) => b.score - a.score)
    .map((e) => e.item);
};


/** Resolve a MAL ID to a TMDB TV show ID using TMDB's cross-reference endpoint.
 *  Falls back to a title search if no MAL link exists in TMDB. */
export const getTmdbIdFromMal = async (
  malId: number,
  titleFallback?: string
): Promise<number | null> => {
  try {
    const res = await tmdb_api.get(`/find/${malId}`, {
      params: {external_source: "mal_id"},
    });
    const tvResults: {id: number}[] = res.data.tv_results ?? [];
    if (tvResults[0]?.id) return tvResults[0].id;
  } catch {
    // continue to fallback
  }

  // Fallback: search TMDB by title (usually finds the right show for anime)
  if (titleFallback) {
    try {
      const res = await tmdb_api.get("/search/tv", {
        params: {...defaultParams, query: titleFallback, page: 1},
      });
      const results: {id: number}[] = res.data.results ?? [];
      return results[0]?.id ?? null;
    } catch {
      return null;
    }
  }

  return null;
};

export const getWatchProviders = async (
  contentType: string,
  contentId: number,
  region: string = "US"
): Promise<{link: string; flatrate?: any[]; rent?: any[]; buy?: any[]} | null> => {
  try {
    const response = await tmdb_api.get(`/${contentType}/${contentId}/watch/providers`, {
      params: defaultParams,
    });
    const results = response.data.results ?? {};
    // Try the requested region; fall back to US so we always show something
    return results[region] ?? (region !== "US" ? results["US"] ?? null : null);
  } catch {
    return null;
  }
};
